import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Alert, Select } from 'antd';
import useSwr from '@hooks/useSwr';
import ActionTypes from '@utils/deviceActions';
import { useTranslation } from 'react-i18next';
import { IDevice } from '@pages/Assets/Inventory/types';
import SessionContext from '@providers/Session/SessionContext';
import { replaceUnderscoresAndCapitalize } from '@utils/formatters';
import SelectCustomers from '@components/SelectCustomers';
import { IContract, IDeviceActionRequestPayload } from '../types';
import useUpload from './Upload';

interface ActionContractMovement {
  actionKey: number;
  uploadedDevicesState: any;
  currentStep: number;
  handleDeviceActions: (item: IDeviceActionRequestPayload) => void;
  manualUpload: any;
}

const useContractMovement = ({
  actionKey,
  uploadedDevicesState,
  currentStep,
  handleDeviceActions,
  manualUpload,
}: ActionContractMovement) => {
  const {
    isRoot,
    selectedApp,
    customer: { customerLogged },
  } = useContext(SessionContext);
  const { t: translate } = useTranslation(['device-actions-modal']);
  const [uploadedDevices, setUploadedDevices] = uploadedDevicesState;
  const [selectedContract, setSelectedContract] = useState<
    string | undefined
  >();
  const [selectedCustomer, setSelectedCustomer] = useState<
    number | undefined
  >();

  const { data: dataContracts } = useSwr<IContract[] | ''>(
    actionKey === ActionTypes.CONTRACT_MOVEMENT &&
      ((isRoot && selectedCustomer) || !isRoot)
      ? '/service-proxy/contracts'
      : null,
    {
      params: {
        customerId: isRoot ? selectedCustomer : customerLogged?.id,
        product: selectedApp?.toUpperCase(),
      },
    },
  );

  const contractOptions = useMemo(
    () =>
      Array.isArray(dataContracts)
        ? dataContracts?.map(({ acronym, assetNumber, nickname }) => ({
            value: assetNumber,
            label: `${assetNumber} ${
              nickname ? ` - ${nickname}` : ''
            }  - ${replaceUnderscoresAndCapitalize(acronym ?? '')}`,
          }))
        : [],
    [dataContracts],
  );

  const { uploadStep, uploadReinitState } = useUpload({
    actionKey,
    setUploadedDevices,
    selectedAcronym: 'MULTICONNECT',
  });

  const contentSteps = useMemo(() => {
    const everyoneStep = [
      {
        visible: isRoot,
        title: translate('steps.client.name'),
        content: (
          <div>
            <SelectCustomers
              value={selectedCustomer}
              onChange={value => {
                setSelectedCustomer(value);
                setSelectedContract(undefined);
              }}
            />
            {selectedCustomer && contractOptions?.length === 0 && (
              <p
                style={{
                  opacity: 0.8,
                  color: 'red',
                  marginTop: 8,
                  marginBottom: 0,
                }}
              >
                {`* ${translate('not-found-contract')}`}
              </p>
            )}
            <Alert
              showIcon
              description={translate('steps.contract-movement.help')}
              style={{ marginTop: 32 }}
            />
          </div>
        ),
      },
      {
        visible: true,
        title: translate('steps.contract.name'),
        content: (
          <div>
            <Select
              loading={!contractOptions}
              optionFilterProp="label"
              options={contractOptions}
              value={selectedContract}
              allowClear
              showSearch
              style={{ width: '100%' }}
              onChange={value => {
                setSelectedContract(value);
              }}
            />
            {!isRoot && (
              <Alert
                showIcon
                description={translate('steps.contract-movement.help')}
                style={{ marginTop: 32 }}
              />
            )}
          </div>
        ),
      },
    ]?.filter(step => step.visible);
    if (manualUpload && manualUpload.length > 0) {
      return everyoneStep;
    }
    return [...everyoneStep, uploadStep];
  }, [
    isRoot,
    translate,
    selectedCustomer,
    contractOptions,
    selectedContract,
    manualUpload,
    uploadStep,
  ]);

  const reinitState = () => {
    uploadReinitState();
    setSelectedContract(undefined);
    setSelectedCustomer(undefined);
  };

  const nextStepIsDisabled = useCallback(() => {
    if (isRoot && currentStep === 0) {
      return (
        !selectedCustomer || !contractOptions || contractOptions?.length === 0
      );
    }
    if (currentStep === (!isRoot ? 0 : 1)) {
      return !selectedContract;
    }

    if (currentStep === (!isRoot ? 1 : 2)) {
      if (manualUpload && manualUpload.length > 0) {
        return false;
      }
      return !uploadedDevices || uploadedDevices.length === 0;
    }

    return false;
  }, [
    isRoot,
    currentStep,
    selectedCustomer,
    contractOptions,
    selectedContract,
    manualUpload,
    uploadedDevices,
  ]);

  const onSubmit = useCallback(async () => {
    const payload: IDeviceActionRequestPayload = {};
    const devicesToSend = uploadedDevices;
    payload.processData = selectedContract;
    payload.action = actionKey;

    if (manualUpload && manualUpload.length > 0) {
      payload.devices = manualUpload.map((item: IDevice) => {
        return {
          msisdn: item.msisdn,
        };
      });
    }
    if (devicesToSend && devicesToSend.length > 0) {
      payload.devices = devicesToSend || [];
    }

    return handleDeviceActions(payload);
  }, [
    selectedContract,
    uploadedDevices,
    handleDeviceActions,
    actionKey,
    manualUpload,
  ]);

  const contractMovementData = {
    contentSteps,
    onSubmit,
    reinitState,
    nextStepIsDisabled,
  };

  return { contractMovementData };
};

export default useContractMovement;
